import { Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { onboardingActions } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-actions/onboarding-actions';
import {
  BaseWidgetProperties,
  ButtonActionType,
  NavigationOptionEnum,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { CloseModalPopupAction } from '../../actions/page-context.action';
import { ConfigService } from '../../services/config-service/config-service.service';
import { KeycloakWrapperService } from '../../services/keycloak-wrapper-service/keycloak-wrapper.service';
import { NavigationService } from '../../services/navigation-service/navigation.service';
import { formatNameAsUrl } from '../zone-url.utils';
import { accountManagementActions } from './cta-actions/account-management-actions';
import { beneficariesActions } from './cta-actions/beneficiaries-actions';
import { beneficiaryFormActions } from './cta-actions/beneficiary-form-actions';
import { businessOnboardingActions } from './cta-actions/business-onboarding-actions';
import { bulkPaymentsActions } from './cta-actions/bulk-payments-actions';
import { cardManagementActions } from './cta-actions/card-management-actions';
import { directorActions } from './cta-actions/director-actions';
import { exchangeActions } from './cta-actions/exchange.cta-actions';
import { feedbackCounterActions } from './cta-actions/feedback-counter-actions';
import { kycVerificationActions } from './cta-actions/kyc-verification-actions';
import { paymentRequestActions } from './cta-actions/payment-request-actions';
import { paymentsActions } from './cta-actions/payments-actions';
import { savingsAccountActions } from './cta-actions/savings-account-actions';
import { supportChatActions } from './cta-actions/support-chat-actions.utils';
import { userProfileActions } from './cta-actions/user-profile-actions';
import { documentActions } from './document-actions';
import { clearFormStateAndRedirect } from './generic-actions/clear-form-state-and-redirect.utils';
import { handleBusinessUserRedirectionOnOnboardingFlows } from './onboarding-zone/business-user-onboarding-redirection-action.utils';
import { runScriptedAction } from './scripted-actions/run-scripted-action';
import { transactionManagementActions } from './cta-actions/transactions-actions';

export type functionCtaType = (
  injector: Injector,
  attri: UiZoneWidgetAttributeConfig,
) => void | Promise<void>;
export type CtaActionGroup = {
  [key: string]: functionCtaType;
};

export const ctaButtonFunctionsMap: Map<ButtonActionType, functionCtaType> = new Map<
  ButtonActionType,
  functionCtaType
>([
  ...Array.from(paymentRequestActions.entries()),
  ...Array.from(bulkPaymentsActions.entries()),
  ...Array.from(beneficariesActions.entries()),
  ...Array.from(kycVerificationActions.entries()),
  ...Array.from(cardManagementActions.entries()),
  ...Array.from(paymentsActions.entries()),
  ...Array.from(userProfileActions.entries()),
  ...Array.from(exchangeActions.entries()),
  ...Array.from(feedbackCounterActions.entries()),
  ...Array.from(accountManagementActions.entries()),
  ...Array.from(transactionManagementActions.entries()),
  ...Array.from(documentActions.entries()),
  ...Array.from(beneficiaryFormActions.entries()),
  ...Array.from(onboardingActions.entries()),
  ...Array.from(savingsAccountActions.entries()),
  ...Array.from(supportChatActions.entries()),
  ...Array.from(directorActions.entries()),
  ...Array.from(businessOnboardingActions.entries()),
  [ButtonActionType.SCRIPTED_ACTION, runScriptedAction],
  [
    ButtonActionType.REDIRECT_BUSINESS_USER_TO_LOGIN,
    (injector: Injector, attri: UiZoneWidgetAttributeConfig) => {
      handleBusinessUserRedirectionOnOnboardingFlows(
        attri.widgetProperties,
        injector.get(Store),
        injector.get(Router),
        injector.get(KeycloakWrapperService),
        injector.get(ConfigService),
      );
    },
  ],
  [
    ButtonActionType.CLEAR_FORM_STATE_AND_REDIRECT,
    (injector: Injector, attri: UiZoneWidgetAttributeConfig) => {
      clearFormStateAndRedirect(injector, attri.widgetProperties);
    },
  ],
]);

/**
 * Redirects the user to a specified page based on widget properties.
 *
 * This function uses the provided Router instance to navigate to a URL
 * determined by the widgetProperties. If a pageId is provided in widgetProperties,
 * it resolves the page metadata and uses NavigationService to build the URL.
 * Otherwise, if an Injector is provided and contains a ConfigService, it uses the
 * service to determine if the navigation should be to a modal.
 *
 * @param router - The Router instance used for navigation.
 * @param widgetProperties - The properties of the widget containing navigation details.
 * @param injector - Optional Injector to retrieve the ConfigService and NavigationService.
 * @param replaceUrl - Optional parameter to determine if the navigation should replace the current URL.
 */
export function redirectToPage(
  router: Router,
  widgetProperties: BaseWidgetProperties,
  injector?: Injector,
  replaceUrl = false,
) {
  /**
   * Initializes the latest page configuration and updates the UI state by conditionally dispatching an action.
   *
   * checks if the current route was opened as a modal (`isModal=true` in query params),
   * and dispatches the `CloseModalPopupAction` accordingly to hide or show the navbar
   * in a mobile container page.
   */
  const activatedRouter = injector?.get(ActivatedRoute);
  const store = injector?.get(Store);

  if (activatedRouter && store) {
    const isPrevModal = activatedRouter?.snapshot?.queryParams?.['isModal'] === 'true';
    store?.dispatch(new CloseModalPopupAction(isPrevModal));
  }

  //bypass as confirmation-code sits outside of the zones so we need to handle it differently
  //without this the reRouteIfModal function doesnt work correctly and messes up the navigation
  if (window.location.pathname === '/confirmation-code') {
    router.navigateByUrl(
      `/zones/${formatNameAsUrl(widgetProperties['zoneToNavigate'])}/${widgetProperties['urlToNavigate']}`,
    );
    return;
  }

  // Handle page navigation metadata if pageId is provided
  const pageId = widgetProperties?.['pageId'];
  if (pageId && injector) {
    const navigationService = injector?.get(NavigationService);
    if (navigationService) {
      const { pageUrl: url } = navigationService.buildPageNavigationUrl({
        pageId: pageId || '', // using this it will generate url based on pageId and ignore pageUrl, if pageId is present in widget config
        zone: widgetProperties?.['zoneToNavigate'] || '',
        pageUrl: widgetProperties?.['urlToNavigate'] || '',
        role: widgetProperties?.['roleToNavigate'] || '',
        navigationOption: widgetProperties?.['navigationOption'] || NavigationOptionEnum.USE_AS_NAVIGATION,
        tabRef: widgetProperties?.['tabRef'] || '',
        pageTransition: widgetProperties?.['pageTransition'] || 'forward',
      });
      router.navigateByUrl(url, { replaceUrl });
      return;
    }
  }

  // Fallback to existing logic
  const configService = injector?.get(ConfigService);
  if (configService) {
    const url = configService.reRouteIfModal(
      widgetProperties['zoneToNavigate'],
      widgetProperties['urlToNavigate'],
    );
    router.navigateByUrl(url, { replaceUrl });
  } else {
    router.navigateByUrl(
      `/zones/${formatNameAsUrl(widgetProperties['zoneToNavigate'])}/${widgetProperties['urlToNavigate']}`,
    );
  }
}
